import { Seo, StructuredData } from '@/components/molecules';
import { ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { type WhatsAppRouteHandle } from '@/hooks/usePageWhatsAppMessage';
import { breadcrumbSchema } from '@/utils/structuredData';

import { GALLERY_WHATSAPP_MESSAGE, gallery, galleryCrumbs } from './content';
import { DeliveryGalleryBand } from './organisms';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle: WhatsAppRouteHandle = { whatsappMessage: GALLERY_WHATSAPP_MESSAGE };

/**
 * Delivered cars — the archive (§2, unblocking A26).
 *
 * The sibling of `/destinations`, and the two are deliberately not one page. The
 * parent answers "will you deliver to me and what does that cost", which is a
 * question with a table for an answer; this one answers "has anyone actually
 * received a car from you", which is a question no table can answer and only
 * photographs can. Folding the wall into the parent would have buried it under
 * a price list, and folding the price list into the wall would have made a
 * gallery open with freight rates.
 *
 * The images and clips are the real archive, read from Supabase and managed
 * from Kabex Hub (`services/deliveries/deliveries.api.ts`) since 2026-10-31.
 *
 * `PageIntro` carries the page's only heading and only action.
 * `keepOnPhone` keeps both on a phone too, because the band below opens
 * straight into its filters rather than restating them: the heading used to
 * repeat verbatim, and the "Explore destinations and routes" button used to
 * sit in the band as a second copy of the one primary action (IA §5)
 * `PageIntro`'s own `action` slot already owns.
 *
 * `compact`, not the plain `slideshow` the narrative pages (About,
 * Destinations) use: this page is a filtered archive grid, the same shape as
 * `/cars` and `/accessories`, not a long-form story, and it benchmarks their
 * intro for the same reason they use it — a visitor opens this page to reach
 * the wall of photos, and a full-height cinematic band between them and it is
 * scroll distance spent on the wrong thing, worst of all on a phone.
 *
 * The page composes and nothing else: one band, which owns its own filters
 * and the two viewers it opens. The one hook here feeds the intro strip,
 * whose figures are counted off the archive rather than written down twice.
 */
export default function DeliveryGalleryPage() {
  // const highlights = useGalleryIntroHighlights();

  return (
    <>
      <StructuredData
        data={[breadcrumbSchema([{ label: 'Home', to: '/' }, ...galleryCrumbs])]}
      />
      <Seo
        title="Delivered cars"
        description="Browse customer handover photos, clips and stories from Kabex Motors. Explore delivery destinations and route enquiries on the destinations page."
        path="/deliveries/gallery"
      />

      <PageIntro
        eyebrow={gallery.eyebrow}
        title={gallery.title}
        // lede={gallery.lede}
        crumbs={galleryCrumbs}
        slideshow
        compact
        keepOnPhone
        // highlights={highlights}
        // action={{ label: 'Explore destinations and routes', to: '/destinations' }}
        // whatsapp
      />

      <DeliveryGalleryBand />

      <ImportPromiseBand />
      <QuoteBand title="Want one like these?" whatsappMessage={GALLERY_WHATSAPP_MESSAGE} />
    </>
  );
}
